/**
 * Which client company a platform role is acting inside.
 *
 * **Visible at all times, not tucked into a menu.** Everything a platform admin does while a
 * company is chosen is done *to that company* — creating its users, issuing its API keys — and the
 * failure this exists to prevent is doing it to the wrong one, which is silent. The bar is where
 * that answer has to be readable without opening anything.
 *
 * Renders **nothing** for a tenant role rather than a disabled control: a client user has exactly
 * one company, and a control implying otherwise is a question they cannot answer.
 *
 * ⚠️ `GET /api/v1/organisations` is gated on `org:admin` (`backend/src/http/routes/admin.ts:502`),
 * which `SUPPORT` does not hold — it has `deca:read` + `org:read`. So the ability is right and this
 * list is not yet reachable for that role. Nothing else in the portal is either: `SUPPORT` has no
 * document surface and no administration surface until the cross-company work, and Paco's matrix
 * ("Support · view companies: all") is what that phase implements. Until then the dropdown shows
 * that role the server's own refusal rather than pretending.
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '../app/errors.ts'
import { useAuth } from '../app/auth.tsx'
import { useOrganisations } from '../app/queries.ts'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { Icon } from '../ui/Icon.tsx'
import { useDebounced } from '../ui/useDebounced.ts'
import { useDismissOnOutsideClick } from '../ui/useDismiss.ts'

/**
 * One page, generous, plus a filter. The contract's maximum is 200 and the default 25 — and a
 * selector that quietly omits companies past the first page would be the wrong-company failure
 * again, arriving as "that client is not in the list".
 */
const PER_PAGE = 50

/** How long typing has to pause before the server is asked, as in the address-book combobox. */
const DEBOUNCE_MS = 250

export function OrganisationSelector() {
  const { t } = useTranslation()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const { organisation, choose } = useOrganisationChoice()

  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  useDismissOnOutsideClick(ref, () => setOpen(false))

  // Every hook sits above the gate below: an early return that skipped one would break the hook
  // order the moment the principal arrives and the ability flips.
  const query = useDebounced(filter.trim(), DEBOUNCE_MS)
  const list = useOrganisations(
    { perPage: PER_PAGE, ...(query ? { q: query } : {}) },
    // Only while the dropdown is open — the choice itself is remembered, so the bar can name the
    // company without the portal fetching a company list on every screen nobody asked it for.
    abilities.choosesOrganisation && open,
    // The last answer stays while the next search runs, so the rows do not blink out per pause.
    { keepPrevious: true },
  )

  if (!abilities.choosesOrganisation) return null

  const rows = list.data?.data ?? []
  const total = list.data?.meta?.total ?? rows.length

  // `org-selector` and its name's class are for the compact top bar (#111), which truncates the name
  // and, on a phone, gives the whole control a row of its own so it stays readable.
  return (
    <div className="user-menu org-selector" ref={ref}>
      <button
        type="button"
        className="user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('organisation.label')}: ${organisation?.name ?? t('organisation.none')}`}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="building" size={18} />
        <span className="small org-selector__name">{organisation?.name ?? t('organisation.none')}</span>
      </button>

      {/* The rows scroll inside the menu, under the filter, and the count of what is not shown
          stays at its foot. With more companies than one page, that line is how a company past the
          first fifty reads as unshown rather than absent; on 2026-10-16 it sat under the last row
          of a menu taller than the screen. */}
      {open ? (
        <div className="menu org-selector__menu" role="menu">
          <span className="menu__label">{t('organisation.choose')}</span>

          <input
            className="input"
            type="search"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t('organisation.filter')}
            aria-label={t('organisation.filter')}
          />

          {organisation ? (
            <button
              type="button"
              role="menuitem"
              className="menu__item"
              onClick={() => {
                choose(null)
                setOpen(false)
              }}
            >
              <Icon name="x" size={16} />
              {t('organisation.clear')}
            </button>
          ) : null}

          <div className="org-selector__list" role="none">
            {list.isLoading ? <span className="menu__label">{t('common.loading')}</span> : null}
            {list.error ? (
              <span className="menu__label">{apiErrorMessage(list.error, t)}</span>
            ) : null}

            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                role="menuitem"
                className="menu__item"
                onClick={() => {
                  choose({ id: row.id, name: row.legalName })
                  setOpen(false)
                }}
              >
                {/* A fixed slot: a bare spacer shrank beside a long name, and the names stopped lining up. */}
                <span className="org-selector__mark">
                  {organisation?.id === row.id ? <Icon name="check" size={16} /> : null}
                </span>
                {row.legalName}
              </button>
            ))}

            {!list.isLoading && !list.error && rows.length === 0 ? (
              <span className="menu__label">{t('organisation.empty')}</span>
            ) : null}
          </div>
          {total > rows.length ? (
            <span className="menu__label org-selector__more">
              {t('organisation.more', { shown: rows.length, total })}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
